import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Collapse,
  Badge,
  Input,
} from "reactstrap";
import style from "./Items.module.scss";
import { CustomFormInput } from "../../../../utils/FormFields";
import { showToast } from "../../../../utils/Helper";

export default function ChangesModal({
  isOpen,
  toggle,
  info = [],
  changedCount = 0,
  riskIndicator = null,
  riskResults = null,
  role = "",
  advisorComments,
  complianceComments,
  fetchs,
  alert,
}) {
  const [expandedRow, setExpandedRow] = useState(null);
  const hiddenFields = ["LegalAddressId", "LegalAddressStateName", "LegalAddressCountryName"];
  const [showComments, setShowComments] = useState(false);
  const [advisorCommentsValue, setAdvisorComments] = useState(advisorComments);
  const [complianceCommentsValue, setComplianceComments] = useState(complianceComments);

  const toggleComments = () => {
    setShowComments((prev) => !prev);
  };

  useEffect(() => {
    setAdvisorComments(advisorComments);
    setComplianceComments(complianceComments);
  }, [advisorComments, complianceComments]);

  const handleSaveComments = () => {
    const body = {
      alertEventId: alert.id,
      role: role,
      SDTComplianceCommentsInOut: {
        advisorComment: advisorCommentsValue,
        complianceComment: complianceCommentsValue,
      },
    };
    fetchs.upsertComplianceEventComments(body, (response) => {
      if (response && response.result.Code == 200) {
        showToast(response.result.Description, false);
      } else {
        showToast("Error saving comments", "error");
      }
    });
  };

  const hiddenSet = new Set((hiddenFields || []).map((f) => String(f).toLowerCase().trim()));

  const filteredInfo = (info || []).filter((item) => {
    if (!item) return false;
    if (!item.key) return true; // keep items without a key
    return !hiddenSet.has(String(item.key).toLowerCase());
  });

  const insertedAttributes = filteredInfo.filter((item) => item.action === "INS");
  const modifiedAttributes = filteredInfo.filter((item) => item.action === "UPD");
  const deletedAttributes = filteredInfo.filter((item) => item.action === "DLT");

  const groupedInsertedAttributes = insertedAttributes.reduce((acc, item) => {
    const group = item.group || "Ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const groupedModifiedAttributes = modifiedAttributes.reduce((acc, item) => {
    const group = item.group || "Ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const groupedDeletedAttributes = deletedAttributes.reduce((acc, item) => {
    const group = item.group || "Ungrouped";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  const toggleRow = (section, group) => {
    const key = `${section}-${group}`;
    setExpandedRow((prevKey) => (prevKey === key ? null : key));
  };

  const GetRiskClassName = (riskTextScore) => {
    const riskLabel = riskTextScore.toLowerCase();
    switch (riskLabel) {
      case "low":
      case "conservative":
        return "green";

      case "medium":
      case "moderate":
        return "orange";

      case "high":
      case "agressive":
        return "red";
      default:
        break;
    }
  };

  const fieldTranslations = {
    LegalAddressCountryName: "Country",
    LegalAddressCountryId: "Country",
    LegalAddressCountryIso: "Country ISO",
    LegalAddressStateName: "State",
    LegalAddressCityName: "City",
    LegalAddressCityId: "City",
    LegalAddressStreet: "Address",
    LegalAddressNumber: "Number",
    LegalAddressZipCode: "Zip Code",
    LegalAddressType: "Type",
    LegalAddressExtraInformation1: "Neighbourhood",
    LegalAddressExtraInformation2: "References",
    LegalAddressReferences: "References",

    PersonActivitySectorId: "Activity Sector",
    PersonProfessionId: "Profession",
    PersonCurrencyType: "Currency Type",
    PersonStatus: "Status",
    PersonRisk: "Risk Level",
    PersonRelatedFrom: "Related From",
    PersoUserGUID: "User GUID",
    PersonObligatedEntity: "Obligated Entity",
    PersonSubAssetClassId: "Sub Asset Class",

    PhysicalPersonSecondName: "Second Name",
    PhysicalPersonCountryOfBirthName: "Country of Birth",
    PhysicalPersonCountryOfBirthId: "Country of Birth",
    PhysicalPersonPublicPositionId: "Public Position",

    SpouseFirstName: "Spouse First Name",
    SpouseSecondName: "Spouse Second Name",
    SpouseFirstLastName: "Spouse Last Name",
    SpouseSecondLastName: "Spouse Second Last Name",
    SpouseDocumentNumber: "Spouse Document Number",

    PersonLinkPublicPositionName: "Link Public Position",
    PersonLinkPublicPositionId: "Link Public Position",
    JuridicalPersonPublicPosition: "Public Position",

    JuridicalPersonConstPlaceId: "Place of Incorporation",
    JuridicalPersonSocietyTypeId: "Society Type",
    JuridicalPersonEntityTypeId: "Entity Type",

    PersonAnualIncome: "Annual Income",
    PersonExtraIncomeVolume: "Annual Extra Income",
    KYCAdvisorComments: "Advisor summary",
    KYCValidateComments: "Compliance summary",
    KYCDate: "KYC Advisor Date",
    KYCValidatedDate: "KYC Compliance Date",
  };

  const generateSummary = (items) => {
    const previousValues = items.map((item) => item.oldValueName || item.oldValue || "-").join(", ");
    const updatedValues = items.map((item) => item.newValueName || item.newValue || "-").join(", ");
    return { previous: previousValues, updated: updatedValues };
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className={style.changesModal}>
      <ModalHeader toggle={toggle}>
        <div
          className={style.headerContent}
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "30px" }}
        >
          <span>Changes ({changedCount})</span>
          {riskIndicator && <span className={style.riskIndicator}>{riskIndicator}</span>}
          {riskResults && (
            <div className={style.riskHeader} style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              {/* Risk Evolution */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                }}
              >
                <span>Risk evolution:</span>
                <Badge
                  style={{
                    backgroundColor: GetRiskClassName(riskResults.OriginTextScore) || "gray",
                    color: "white",
                    width: "90px",
                    padding: "5px 8px",
                    textAlign: "center",
                    textTransform: "capitalize",
                    fontSize: "0.8rem",
                  }}
                >
                  {riskResults.OriginTextScore || "N/A"}
                </Badge>
                <span style={{ fontSize: "1rem" }}>→</span>
                <Badge
                  style={{
                    backgroundColor: GetRiskClassName(riskResults.FutureTextScore) || "gray",
                    color: "white",
                    width: "90px",
                    padding: "5px 8px",
                    textAlign: "center",
                    textTransform: "capitalize",
                    fontSize: "0.8rem",
                  }}
                >
                  {riskResults.FutureTextScore || "N/A"}
                </Badge>
              </div>

              {/* Score Evolution */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "0.9rem",
                  fontWeight: "bold",
                }}
              >
                <span>Score evolution:</span>
                <strong style={{ fontSize: "0.9rem" }}>
                  {riskResults.OriginNumericScore || 0} → {riskResults.FutureNumericScore || 0}
                </strong>
              </div>
            </div>
          )}
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={style.modalScrollArea}>
          {/* Inserted Risk Attributes */}
          {insertedAttributes.length > 0 && (
            <div className={`${style.insertedSection} unifiedFont`}>
              <h6>Inserted Risk Attributes</h6>
              <Table className={style.table}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>New value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedInsertedAttributes).map(([group, items]) => {
                    const isComposite = group !== "Ungrouped";
                    const summary = isComposite ? generateSummary(items) : null;

                    return (
                      <React.Fragment key={group}>
                        {isComposite ? (
                          <React.Fragment>
                            <tr onClick={() => toggleRow("inserted", group)} className={style.clickableRow}>
                              <td>{group}</td>
                              <td className={style.newValue}>{summary.updated}</td>
                            </tr>
                            {expandedRow === `inserted-${group}` && (
                              <tr>
                                <td colSpan={2}>
                                  <Collapse isOpen={expandedRow === `inserted-${group}`}>
                                    <div className={style.expandedRow}>
                                      <Table className={style.detailsTable}>
                                        <thead>
                                          <tr>
                                            <th>Field</th>
                                            <th>New Value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {items.map((item) => (
                                            <tr key={item.key}>
                                              <td>{fieldTranslations[item.key] || item.key}</td>
                                              <td className={style.newValue}>
                                                {item.newValueName || item.newValue || "-"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ) : (
                          items.map((item) => (
                            <tr key={item.key}>
                              <td>{fieldTranslations[item.key] || item.key}</td>
                              <td className={style.newValue}>{item.newValueName || item.newValue || "-"}</td>
                            </tr>
                          ))
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {/* Modified Risk Attributes */}
          {modifiedAttributes.length > 0 && (
            <div className={style.modifiedSection}>
              <h6>Modified Risk Attributes</h6>
              <Table className={style.table}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Current value</th>
                    <th>Updated value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedModifiedAttributes).map(([group, items]) => {
                    const isComposite = group !== "Ungrouped";
                    const summary = isComposite ? generateSummary(items) : null;

                    return (
                      <React.Fragment key={group}>
                        {isComposite ? (
                          <React.Fragment>
                            <tr onClick={() => toggleRow("modified", group)} className={style.clickableRow}>
                              <td>{group}</td>
                              <td className={style.prevValue}>{summary.previous}</td>
                              <td className={style.newValue}>{summary.updated}</td>
                            </tr>
                            {expandedRow === `modified-${group}` && (
                              <tr>
                                <td colSpan={3}>
                                  <Collapse isOpen={expandedRow === `modified-${group}`}>
                                    <div className={style.expandedRow}>
                                      <Table className={style.detailsTable}>
                                        <thead>
                                          <tr>
                                            <th>Field</th>
                                            <th>Current value</th>
                                            <th>Updated value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {items.map((item) => (
                                            <tr key={item.key}>
                                              <td>{fieldTranslations[item.key] || item.key}</td>
                                              <td
                                                className={
                                                  item.oldValueName === item.newValueName
                                                    ? style.unchangedValue
                                                    : style.prevValue
                                                }
                                              >
                                                {item.oldValueName || item.oldValue || "-"}
                                              </td>
                                              <td
                                                className={
                                                  item.oldValueName === item.newValueName
                                                    ? style.unchangedValue
                                                    : style.newValue
                                                }
                                              >
                                                {item.newValueName || item.newValue || "-"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ) : (
                          items.map((item) => (
                            <tr key={item.key}>
                              <td>{fieldTranslations[item.key] || item.key}</td>
                              <td className={style.prevValue}>{item.oldValueName || item.oldValue || "-"}</td>
                              <td className={style.newValue}>{item.newValueName || item.newValue || "-"}</td>
                            </tr>
                          ))
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {/* Deleted Risk Attributes */}
          {deletedAttributes.length > 0 && (
            <div className={style.deletedSection}>
              <h6>Deleted Risk Attributes</h6>
              <Table className={style.table}>
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Current value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedDeletedAttributes).map(([group, items]) => {
                    const isComposite = group !== "Ungrouped";
                    const summary = isComposite ? generateSummary(items) : null;

                    return (
                      <React.Fragment key={group}>
                        {isComposite ? (
                          <React.Fragment>
                            <tr onClick={() => toggleRow("deleted", group)} className={style.clickableRow}>
                              <td>{group}</td>
                              <td className={style.prevValue}>{summary.previous}</td>
                            </tr>
                            {expandedRow === `deleted-${group}` && (
                              <tr>
                                <td colSpan={2}>
                                  <Collapse isOpen={expandedRow === `deleted-${group}`}>
                                    <div className={style.expandedRow}>
                                      <Table className={style.detailsTable}>
                                        <thead>
                                          <tr>
                                            <th>Field</th>
                                            <th>Current value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {items.map((item) => (
                                            <tr key={item.key}>
                                              <td>{fieldTranslations[item.key] || item.key}</td>
                                              <td className={style.prevValue}>
                                                {item.oldValueName || item.oldValue || "-"}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </Collapse>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ) : (
                          items.map((item) => (
                            <tr key={item.key}>
                              <td>{fieldTranslations[item.key] || item.key}</td>
                              <td className={style.prevValue}>{item.oldValueName || item.oldValue || "-"}</td>
                            </tr>
                          ))
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </ModalBody>
      {showComments && (
        <div>
          <hr style={{ height: "0.5px", backgroundColor: "#6c757d", border: "none", margin: "2px 0" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                fontSize: "0.9rem",
                fontWeight: "light",
                marginLeft: "12px",
              }}
            >
              <CustomFormInput>
                <span style={{ marginBottom: "10px", display: "block" }}>Advisor summary:</span>
                <Input
                  type="textarea"
                  style={{
                    height: "90px",
                    width: "382px",
                    overflow: "auto",
                  }}
                  value={advisorCommentsValue}
                  onChange={(e) => setAdvisorComments(e.target.value)}
                  readOnly={role === "Compliance"}
                />
              </CustomFormInput>
            </div>
            <div
              style={{
                fontSize: "0.9rem",
                fontWeight: "light",
              }}
            >
              <CustomFormInput>
                <span style={{ marginBottom: "10px", display: "block" }}>Compliance summary:</span>{" "}
                <Input
                  type="textarea"
                  style={{
                    height: "90px",
                    width: "382px",
                    overflow: "auto",
                  }}
                  value={complianceCommentsValue}
                  onChange={(e) => setComplianceComments(e.target.value)}
                  readOnly={role === "Advisor"}
                />
              </CustomFormInput>
            </div>
          </div>{" "}
        </div>
      )}
      <ModalFooter>
        {/* onClick={onSaveComments} */}
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button color="link" onClick={toggleComments} className={style.toggleCommentsButton}>
            {showComments ? "Hide comments" : "Display comments"}
          </Button>
          <div>
            {showComments && (
              <Button color="primary" onClick={handleSaveComments} style={{ marginRight: "10px" }}>
                Save comments
              </Button>
            )}
            <Button color="secondary" onClick={toggle}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
